/* CM sticky Add to Cart — the bar element (<hs-sticky-atc>) and the button that mirrors the buy
   box (<hs-atc-proxy>). Shared by the CM PDP (with hs-pdp.js) and every other product page (with
   cm-sticky-theme.js). Loaded with `defer`; safe to include more than once. */
(() => {
  if (window.__cmSticky) return;
  window.__cmSticky = true;

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = reduceMotion ? 'auto' : 'smooth';

  function jump(target) {
    const el = typeof target === 'string' ? $(target) : target;
    if (!el) return;
    el.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
    settle(el);
  }
  // From the top of the page the store header isn't sticky yet, so the scroll offset is measured
  // without it and the target lands under the header. Once scrolling stops, nudge it into place.
  function settle(el) {
    let t, done = false;
    const stop = () => { done = true; clearTimeout(t); removeEventListener('scroll', onScroll); };
    const fix = () => {
      if (done) return; stop();
      const want = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
      const off = el.getBoundingClientRect().top - want;
      if (Math.abs(off) > 4) scrollBy({ top: off, behavior: scrollBehavior });
    };
    const onScroll = () => { clearTimeout(t); t = setTimeout(fix, 150); };
    addEventListener('scroll', onScroll, { passive: true });
    t = setTimeout(fix, 600);                 // no scroll happened (already there)
    setTimeout(stop, 4000);                   // never chase the page for long
  }

  /* ---------- <hs-atc-proxy>: any button that mirrors the buy box (final CTA) ---------- */
  class AtcProxy extends HTMLElement {
    connectedCallback() {
      this.btn = $('[data-hs-proxy-btn]', this) || $('button', this);
      this.idle = this.btn.dataset.idle || this.btn.textContent;
      document.addEventListener('hs:state', e => this.update(e.detail));
      this.btn.addEventListener('click', () => {
        if (this.ready) document.dispatchEvent(new Event('hs:submit'));
        else jump(this.dataset.fallback || '#hs-buy-box');   // theme product pages: their own options
      });
    }
    update(s) {
      this.ready = s.ready;
      this.btn.textContent = s.ready ? s.label : this.idle;
    }
  }
  define('hs-atc-proxy', AtcProxy);

  /* ---------- <hs-sticky-atc>: our own sticky bar ----------
     Mirrors the buy box: options and quantity chosen here are applied to the buy box (one source
     of truth, so GFCI rules, stock caps and the ship note all still apply), and the buy box's
     state flows back here. Settings on the Add to Cart block control what it shows. */
  define('hs-sticky-atc', class extends AtcProxy {
    connectedCallback() {
      super.connectedCallback();
      // Anchor: the buy box's Add to Cart. On theme product pages (no CM buy box) the bar is
      // told which button to watch, and cm-theme-product stands in for the buy box.
      const anchor = $(this.dataset.anchor || '[data-hs-atc]');
      if (!anchor) return;
      this.box = document.querySelector('hs-product, cm-theme-product');
      const phone = matchMedia('(max-width: 749px)');
      const desktopOn = this.dataset.desktop !== 'false';
      let io;
      const watch = () => {
        io?.disconnect();
        if (!phone.matches && !desktopOn) { this.show(false); return; }
        // Default: shows once the main button is above the viewport top. Phones set to "early":
        // shows whenever the main button is off screen, including on first load.
        const early = this.dataset.mobile === 'early' && phone.matches;
        io = new IntersectionObserver(([en]) => this.show(!en.isIntersecting),
          { rootMargin: early ? '0px' : '0px 0px 100000px 0px' });
        io.observe(anchor);
      };
      phone.addEventListener?.('change', watch);
      watch();

      this.addEventListener('change', e => {
        if (e.target.matches('[data-hs-sticky-gfci]')) this.box?.setGfci(e.target.value);
        const opt = e.target.closest('[data-hs-sticky-opt]');
        if (opt) this.box?.setOption(+opt.dataset.hsStickyOpt, opt.value);
        if (e.target.matches('[data-hs-sticky-qty]')) this.box?.setQty(e.target.value);
      });
      const panel = $('[data-hs-sticky-panel]', this);
      // Phones/tablets: Add to Cart that can't add yet (power unit unchosen, or the chosen
      // Model/Size isn't available) opens the choice panel right here instead of scrolling away.
      // Runs in the capture phase, before the button's own handler.
      this.addEventListener('click', e => {
        if (!e.target.closest('[data-hs-proxy-btn]') || this.ready || this.soldOut || !panel) return;
        if (!matchMedia('(max-width: 989px)').matches) return;
        e.stopPropagation(); e.preventDefault();
        togglePanel(true);
      }, true);
      const togglePanel = this.togglePanel = open => {
        if (!panel) return;
        panel.hidden = !open;
        $$('[data-hs-sticky-change]', this).forEach(b => b.setAttribute('aria-expanded', String(!!open)));
        document.documentElement.classList.toggle('hs-sk-panel-open', !!open);   // keeps the chat button off the choices
      };
      // Tap outside the bar, or Escape, closes the panel
      document.addEventListener('click', e => { if (panel && !panel.hidden && !this.contains(e.target)) togglePanel(false); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape' && panel && !panel.hidden) togglePanel(false); });
      this.addEventListener('click', e => {
        if (e.target.closest('[data-hs-sticky-change]')) { togglePanel(panel?.hidden); return; }
        if (e.target.closest('[data-hs-sticky-close]')) { togglePanel(false); return; }
        const choice = e.target.closest('[data-hs-sticky-gfci-btn]');
        if (choice) { this.box?.setGfci(choice.dataset.hsStickyGfciBtn); togglePanel(false); return; }
        const step = e.target.closest('[data-hs-sticky-step]');
        if (step && this.box) this.box.setQty((+this.box.qtyInput.value || 1) + +step.dataset.hsStickyStep);
      });
      if (this.box?.d) this.box.render();
    }
    show(on) {
      if (!on) this.togglePanel?.(false);
      this.classList.toggle('is-visible', on);
      this.setAttribute('aria-hidden', String(!on));
      this.inert = !on;
      document.documentElement.classList.toggle('hs-sticky-on', on && this.dataset.position !== 'top');
    }
    update(s) {
      this.ready = s.ready;
      this.soldOut = !!s.soldOut;
      $$('[data-hs-sticky-summary]', this).forEach(el => { el.textContent = s.summary || (el.closest('[data-hs-sticky-change]') ? 'Choose options' : ''); });
      const p = $('[data-hs-sticky-price]', this);
      if (p) { p.textContent = s.price; p.classList.toggle('is-sale', s.onSale); }
      (s.options || []).forEach((val, i) => {
        $$(`[data-hs-sticky-opt="${i}"]`, this).forEach(sel => { if (val != null && sel.value !== val) sel.value = val; });
      });
      const q = $('[data-hs-sticky-qty]', this);
      if (q) {
        if (s.qty) q.value = s.qty;
        q.max = s.max || 99;
        $$('[data-hs-sticky-step="1"]', this).forEach(b => { b.disabled = (s.qty || 1) >= (s.max || 99); });
        $$('[data-hs-sticky-step="-1"]', this).forEach(b => { b.disabled = (s.qty || 1) <= 1; });
      }
      // Power unit: the add-on line, the desktop dropdown and the phone panel all follow the buy box
      const addon = $('[data-hs-sticky-addon]', this);
      const text = s.addon || (s.hasGfci ? (s.gfci === 'have' ? 'Power unit: I already have one' : 'Power unit: choose one') : '');
      if (addon) { addon.textContent = text; addon.hidden = !text; addon.classList.toggle('is-alert', !!s.needsChoice); }
      const gval = $('[data-hs-sticky-gfci-value]', this);
      if (gval) { gval.textContent = s.gfciShort || ''; $('[data-hs-sticky-change]', this)?.classList.toggle('is-alert', !!s.needsChoice); }
      const gsel = $('[data-hs-sticky-gfci]', this);
      if (gsel) {
        gsel.value = s.gfci || '';
        const addOpt = gsel.querySelector('option[value="add"]');
        // Short label in the bar's dropdown: "Power unit +$84.00"
        if (addOpt) { addOpt.textContent = (s.gfciAddLabel || addOpt.textContent).replace(/^Add power unit \(\+(.*)\)$/, 'Power unit +$1'); addOpt.disabled = !!s.gfciAddDisabled; }
      }
      $$('[data-hs-sticky-gfci-btn]', this).forEach(b => {
        b.setAttribute('aria-pressed', String(b.dataset.hsStickyGfciBtn === s.gfci));
        if (b.dataset.hsStickyGfciBtn === 'add') { b.textContent = s.gfciAddLabel || b.textContent; b.disabled = !!s.gfciAddDisabled; }
      });
      const img = $('[data-hs-sticky-img]', this);
      if (img && s.image && img.getAttribute('src') !== s.image) img.src = s.image;
      const label = this.btn.dataset.label || 'Add to Cart';
      // Narrow screens keep the short label; tapping it opens the power unit panel instead (below).
      const narrow = matchMedia('(max-width: 989px)').matches;
      this.needsChoice = !!s.needsChoice;
      this.btn.textContent = s.soldOut ? 'Sold out' : s.ready ? label
        : s.needsChoice ? (narrow && $('[data-hs-sticky-panel]', this) ? label : 'Choose power option') : (this.btn.dataset.idle || 'Choose options');
      this.btn.disabled = s.soldOut;
    }
  });
})();
